import { useAppStore } from 'hooks/state/useAppStore';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { useModelService } from 'services/model/model.service';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { ExpandableRow } from 'components/table/ExpandableRow';
import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { useIntl } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { DataEntryRowRecord, useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { createExpanderCell } from 'components/table/DataTableUtil';

interface CountryChannelBreakdownRow extends ExpandableRow<CountryChannelBreakdownRow>, DataEntryRowRecord {
  share: number;
}

const columnHelper = createColumnHelper<CountryChannelBreakdownRow>();
const useColumns = () => {
  const { createMeasureColumn } = useDataEntriesInTables();
  const { getMeasuresByMetrics, getAttribute } = useModelService();
  const intl = useIntl();
  const [ordGmvVal, ordNum, avgOrdGmvVal, reaRetQtyRate, reaNetSlsGmvVal] = getMeasuresByMetrics(
    ['ordGmvVal', 'ordNum', 'avgOrdGmvVal', 'retQtyRate', 'netSlsGmvVal'],
    'OD'
  );
  return [
    columnHelper.accessor('chnCountry', {
      header: () => getAttribute('chnCountry').name,
      cell: context =>
        createExpanderCell(context as CellContext<CountryChannelBreakdownRow, string>, <>{context.getValue()}</>, true),
    }),

    columnHelper.accessor('share', {
      header: () => intl.formatMessage({ id: 'insights.report.channelShare' }),
      cell: context => (
        <ShareBar width={context.getValue() * 100} color={context.row.getCanExpand() ? '#314968' : '#5CBBC7'} />
      ),
    }),
    createMeasureColumn(ordGmvVal),
    createMeasureColumn(ordNum),
    createMeasureColumn(avgOrdGmvVal),
    createMeasureColumn(reaRetQtyRate),
    createMeasureColumn(reaNetSlsGmvVal),
  ];
};

export function CountryChannelBreakdownTable() {
  const { products } = useAppStore();
  const { fetchData } = useProDataService(Service.Countries);
  const requestData = useDataRequest();
  const { dataEntryToDataEntryRowRecord } = useDataEntriesInTables();

  const { getMeasuresByMetrics, getAttributes } = useModelService();

  const [chnCountry, chnName] = getAttributes(['chnCountry', 'chnName']);

  const measures = getMeasuresByMetrics(['ordGmvVal', 'ordNum', 'avgOrdGmvVal', 'retQtyRate', 'netSlsGmvVal'], 'OD');

  const [ordGmvVal] = measures;
  const productNumbers = products?.map(p => p);

  const columns = useColumns() as ColumnDef<CountryChannelBreakdownRow>[];

  const reportState = useDataServiceQueryResult(
    requestData,
    baseData =>
      fetchData('reports/channel-breakdown', {
        ...baseData,
        filters: {
          ...baseData.filters,
          productNumbers,
        },
      }),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );

      return {
        isComparisonActive,
        response,
      };
    },
    ['insights-countries-channel-breakdown-report']
  );

  if (!reportState.data && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const rows =
    reportState.data?.response.sets.countries.map(row => {
      const countryRow: CountryChannelBreakdownRow = {
        ...dataEntryToDataEntryRowRecord(row),
        // We have overloaded the country column to show either country or Channel and hence are not using the
        // default country formatting. Extract here the value again and translate rather than use the code inserted by
        // dataEntryToDataEntryRowRecord
        chnCountry: chnCountry.getValue(row.attributes),
        share:
          ordGmvVal.getValue(row.values) /
          (ordGmvVal.getValue(reportState.data!.response.values) ?? ordGmvVal.getValue(row.values)),
        subRows: [],
      };

      countryRow.subRows = row.sets.channels.map(subRow => {
        const channelRow: CountryChannelBreakdownRow = {
          chnCountry: chnName.getValue(subRow.attributes),
          share:
            ordGmvVal.getValue(subRow.values) / (ordGmvVal.getValue(row.values) ?? ordGmvVal.getValue(subRow.values)),

          ...dataEntryToDataEntryRowRecord(subRow),
          subRows: [],
        };

        return channelRow;
      });

      return countryRow;
    }) ?? [];

  const exportConfigOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.Countries,
      measuresGroup: 'channel-breakdown',
      tableName: 'Country Breakdown',
      isProReport: true,
    },
  };

  return (
    <DataTableContextProvider>
      <DataTable
        titleId="insights.country.reports.channel_breakdown.description"
        rows={rows}
        columns={columns}
        defaultSortingState={[{ id: measures[0].code, desc: true }]}
        exportConfigOptions={exportConfigOptions}
        isError={reportState.isError}
        errorMessage={reportState.error}
      />
    </DataTableContextProvider>
  );
}
